// components/CreateTicket.jsx
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppServices } from "../../../hook/services";
import { toast } from "react-toastify";

const CreateTicket = () => {
  const AppService = useAppServices();
  const { companyId, locationId, userId, email, userName } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    loomVideoUrl: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    } else if (formData.subject.length < 5) {
      newErrors.subject = "Subject must be at least 5 characters";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.length < 10) {
      newErrors.description =
        "Please provide more details (min. 10 characters)";
    }

    if (formData.loomVideoUrl && !isValidLoomUrl(formData.loomVideoUrl)) {
      newErrors.loomVideoUrl = "Please enter a valid Loom video URL";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidLoomUrl = (url) => {
    const loomRegex =
      /^(https?:\/\/)?(www\.)?loom\.com\/(share|embed)\/[a-zA-Z0-9]+$/;
    return loomRegex.test(url.trim());
  };

  const generateTicketId = () => {
    return "TKT-" + Math.random().toString(36).substr(2, 9).toUpperCase();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the highlighted errors before submitting.");
      return;
    }

    if (!locationId || !companyId) {
      toast.error(
        "Missing account context. Please reload the page and try again."
      );
      return;
    }

    const ticket_id = generateTicketId();
    const payload = {
      subject: formData.subject.trim(),
      description: formData.description.trim(),
      loomVideoUrl: formData.loomVideoUrl.trim(),
      locationId,
      userId,
      email,
      userName,
      companyId,
      ticket_id,
    };

    setIsSubmitting(true);
    try {
      const { response, error } = await AppService.support.create_ticket({
        payload,
      });

      if (error || !response) {
        const description =
          error?.message ||
          error?.response?.data?.message ||
          response?.message ||
          "Couldn't create the ticket. Please try again.";
        toast.error(description);
        return;
      }

      toast.success(`Ticket ${ticket_id} created successfully!`);
      navigate(-1);
    } catch (err) {
      console.error("Error creating ticket:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Something went wrong while creating the ticket."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const extractLoomId = (url) => {
    const match = url.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/);
    return match ? match[1] : null;
  };

  const isFormEmpty =
    !formData.subject && !formData.description && !formData.loomVideoUrl;
  const descLen = formData.description.length;

  return (
    <div className="ct-page">
      <div className="ct-shell">
        {/* Top row: back + clear */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="ct-back-btn d-inline-flex align-items-center bg-transparent border-0 p-0 text-secondary"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>Back</span>
          </button>
          {!isFormEmpty && !isSubmitting && (
            <button
              type="button"
              className="ct-reset-link d-inline-flex align-items-center"
              onClick={() => {
                setFormData({ subject: "", description: "", loomVideoUrl: "" });
                setErrors({});
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                <polyline points="1 4 1 10 7 10" />
                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
              </svg>
              Clear all
            </button>
          )}
        </div>

        {/* Header */}
        <header className="mb-3">
          <h3 className="ct-title mb-1" style={{ fontSize: "1.375rem", fontWeight: 700, letterSpacing: "-0.015em", lineHeight: 1.15, color: "#0f172a" }}>
            Create support ticket
          </h3>
          <p className="ct-subtitle mb-0">
            Tell us what's going on and we'll get back to you as soon as we can.
          </p>
        </header>

        {/* Form card */}
        <div className="ct-card">
          <form onSubmit={handleSubmit}>
            <div className="ct-card-body">
              {/* Subject */}
              <div className="ct-field">
                <label htmlFor="subject" className="ct-label d-flex justify-content-between align-items-center">
                  <span>
                    Subject <span className="ct-required">*</span>
                  </span>
                </label>
                <input
                  type="text"
                  className={`ct-input ${errors.subject ? "is-invalid" : ""}`}
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder='e.g. "Unable to upload files larger than 10MB"'
                  autoFocus
                />
                {errors.subject && (
                  <div className="ct-error d-flex align-items-center">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {errors.subject}
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="ct-field">
                <label htmlFor="description" className="ct-label d-flex justify-content-between align-items-center">
                  <span>
                    Description <span className="ct-required">*</span>
                  </span>
                  <span className={`ct-counter ${descLen > 0 && descLen < 10 ? "is-low" : ""}`}>
                    {descLen} chars
                  </span>
                </label>
                <textarea
                  className={`ct-input ct-textarea ${errors.description ? "is-invalid" : ""}`}
                  id="description"
                  name="description"
                  rows="5"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Steps to reproduce · expected vs. actual · error messages · browser/device"
                />
                {errors.description && (
                  <div className="ct-error d-flex align-items-center">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {errors.description}
                  </div>
                )}
              </div>

              {/* Loom URL */}
              <div className="ct-field mb-0">
                <label htmlFor="loomVideoUrl" className="ct-label d-flex justify-content-between align-items-center">
                  <span>
                    Loom video <span className="ct-optional">Optional</span>
                  </span>
                  <a
                    href="https://www.loom.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ct-tip-link"
                    title="Recording a Loom is the fastest way to explain technical issues"
                  >
                    Don't have Loom? Get it →
                  </a>
                </label>
                <input
                  type="url"
                  className={`ct-input ${errors.loomVideoUrl ? "is-invalid" : ""}`}
                  id="loomVideoUrl"
                  name="loomVideoUrl"
                  value={formData.loomVideoUrl}
                  onChange={handleChange}
                  placeholder="https://www.loom.com/share/..."
                />
                {errors.loomVideoUrl && (
                  <div className="ct-error d-flex align-items-center">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {errors.loomVideoUrl}
                  </div>
                )}

                {/* Preview */}
                {formData.loomVideoUrl &&
                  !errors.loomVideoUrl &&
                  isValidLoomUrl(formData.loomVideoUrl) && (
                    <div className="ct-preview mt-3">
                      <div className="ct-preview-frame ratio ratio-16x9">
                        <iframe
                          src={`https://www.loom.com/embed/${extractLoomId(
                            formData.loomVideoUrl
                          )}`}
                          frameBorder="0"
                          allowFullScreen
                          title="Loom video player"
                        ></iframe>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <small className="ct-preview-hint d-inline-flex align-items-center">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          Will be attached to the ticket
                        </small>
                        <button
                          type="button"
                          className="ct-link-btn"
                          onClick={() =>
                            setFormData((prev) => ({ ...prev, loomVideoUrl: "" }))
                          }
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
              </div>
            </div>

            {/* Actions */}
            <div className="ct-card-footer d-flex justify-content-end align-items-center gap-2">
              <button
                type="button"
                className="ct-btn ct-btn--ghost"
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="ct-btn ct-btn--primary"
                disabled={
                  isSubmitting ||
                  !formData.subject.trim() ||
                  !formData.description.trim()
                }
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                    Creating…
                  </>
                ) : (
                  <>
                    Submit ticket
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="ms-1">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .ct-page { font-family: 'Inter', sans-serif; }
        .ct-shell {
          max-width: 720px;
          margin: 0 auto;
        }

        .ct-back-btn { transition: color 0.15s ease; }
        .ct-back-btn:hover { color: #1f2937 !important; }
        .ct-back-btn:hover svg { transform: translateX(-2px); }
        .ct-back-btn svg { transition: transform 0.15s ease; }

        .ct-reset-link {
          background: transparent;
          border: 0;
          padding: 4px 8px;
          color: #6b7280;
          font-size: 0.75rem;
          font-weight: 500;
          cursor: pointer;
          border-radius: 6px;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .ct-reset-link:hover { background: #f3f4f6; color: #1f2937; }

        .ct-subtitle {
          font-size: 0.8125rem;
          color: #6b7280;
          line-height: 1.5;
        }

        /* Form card */
        .ct-card {
          background: #fff;
          border: 1px solid #ececec;
          border-radius: 14px;
          overflow: hidden;
        }
        .ct-card-body { padding: 20px 22px; }
        .ct-card-footer {
          padding: 12px 22px;
          border-top: 1px solid #f1f1f1;
          background: #fafafa;
        }

        /* Fields */
        .ct-field { margin-bottom: 16px; }
        .ct-label {
          display: block;
          font-size: 0.8125rem;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 6px;
        }
        .ct-required {
          color: #dc2626;
          margin-left: 2px;
          font-weight: 700;
        }
        .ct-optional {
          font-size: 0.6875rem;
          font-weight: 500;
          color: #6b7280;
          background: #f3f4f6;
          padding: 1px 6px;
          border-radius: 4px;
          margin-left: 6px;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }
        .ct-counter {
          font-size: 0.6875rem;
          font-weight: 500;
          color: #9ca3af;
          letter-spacing: 0.01em;
          transition: color 0.15s ease;
        }
        .ct-counter.is-low { color: #d97706; }
        .ct-error {
          font-size: 0.75rem;
          color: #b91c1c;
          margin-top: 6px;
          font-weight: 500;
        }
        .ct-input {
          width: 100%;
          height: 40px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 0 12px;
          background: #fff;
          font-size: 0.875rem;
          color: #1f2937;
          font-family: inherit;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }
        .ct-input::placeholder { color: #9ca3af; }
        .ct-input:focus {
          outline: none;
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }
        .ct-input.is-invalid {
          border-color: #fca5a5;
          background: #fef2f2;
        }
        .ct-input.is-invalid:focus {
          border-color: #dc2626;
          box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
        }
        .ct-textarea {
          height: auto;
          padding: 10px 12px;
          line-height: 1.5;
          resize: vertical;
          min-height: 110px;
          font-family: inherit;
        }

        /* Loom helper link in label row */
        .ct-tip-link {
          font-size: 0.75rem;
          font-weight: 500;
          color: #4f46e5;
          text-decoration: none;
          transition: color 0.15s ease;
        }
        .ct-tip-link:hover { color: #4338ca; text-decoration: none; }

        /* Preview */
        .ct-preview { }
        .ct-preview-frame {
          border-radius: 10px;
          overflow: hidden;
          background: #0f172a;
          border: 1px solid #e5e7eb;
        }
        .ct-preview-hint {
          font-size: 0.75rem;
          color: #15803d;
          font-weight: 500;
        }

        /* Inline remove link */
        .ct-link-btn {
          background: transparent;
          border: 0;
          padding: 2px 6px;
          color: #4f46e5;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          border-radius: 6px;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .ct-link-btn:hover { background: #eef2ff; color: #4338ca; }

        /* Buttons */
        .ct-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.005em;
          padding: 9px 18px;
          border-radius: 9px;
          border: 1px solid transparent;
          line-height: 1;
          cursor: pointer;
          font-family: inherit;
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .ct-btn:disabled { opacity: 0.55; cursor: not-allowed; transform: none !important; }
        .ct-btn:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        }
        .ct-btn--ghost {
          background: transparent;
          border-color: transparent;
          color: #6b7280;
        }
        .ct-btn--ghost:hover:not(:disabled) {
          background: #f3f4f6;
          color: #1f2937;
        }

        .ct-btn--primary {
          background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.04);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(79, 70, 229, 0.22),
            0 4px 10px rgba(79, 70, 229, 0.2);
        }
        .ct-btn--primary:hover:not(:disabled) {
          background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
          transform: translateY(-1px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 2px 4px rgba(67, 56, 202, 0.28),
            0 8px 18px rgba(79, 70, 229, 0.3);
        }
        .ct-btn--primary:active:not(:disabled) { transform: translateY(0); }
      `}</style>
    </div>
  );
};

export default CreateTicket;
